/**
 * Column reference utilities
 * Maps headers to reference labels (A, B, C for groups or 1, 2, 3 for individual columns)
 */

import { DimensionType } from '@/types/rebateAnalytics';

/**
 * Build column reference map (A, B, C for groups or 1, 2, 3 for individual columns)
 */
export function buildColumnReferenceMap(
  headers: string[],
  secondaryXDimension?: DimensionType,
): Map<string, string | string[]> {
  const map = new Map<string, string | string[]>();

  if (secondaryXDimension) {
    // Group headers: A, B, C, ...
    // Extract unique group names (month names before " - ")
    const groupNames = new Set<string>();
    const groupToHeaders = new Map<string, string[]>();

    headers.forEach((header) => {
      const parts = header.split(' - ');
      if (parts.length === 2) {
        const groupName = parts[0];
        groupNames.add(groupName);
        if (!groupToHeaders.has(groupName)) {
          groupToHeaders.set(groupName, []);
        }
        groupToHeaders.get(groupName)!.push(header);
      }
    });

    const sortedGroups = Array.from(groupNames).sort((a, b) => {
      const aIndex = headers.findIndex((h) => h.startsWith(`${a} - `));
      const bIndex = headers.findIndex((h) => h.startsWith(`${b} - `));
      return aIndex - bIndex;
    });

    sortedGroups.forEach((groupName, index) => {
      const letter = String.fromCharCode(65 + index); // 65 = 'A'
      map.set(letter, groupToHeaders.get(groupName) || []);
    });
  } else {
    // Individual headers: 1, 2, 3, ...
    headers.forEach((header, index) => {
      map.set(String(index + 1), header);
    });
  }

  return map;
}

/**
 * Resolve column reference to actual header name(s)
 */
export function resolveColumnReference(
  reference: string,
  columnMap: Map<string, string | string[]>,
): string | string[] | null {
  return columnMap.get(reference) || null;
}
